import React, { useState, useEffect } from 'react';
import { Clock, MessageSquare, CheckCircle, AlertCircle, Play, Pause, RotateCcw, Timer, HelpCircle } from 'lucide-react';

interface ScriptPhaseProps {
  phase: {
    id: string;
    name: string;
    duration: number;
    content: string;
    formatted_content?: string;
    questions: string[];
    is_required: boolean;
  };
  phaseIndex: number;
  isActive: boolean;
  note: string;
  onNoteChange: (note: string) => void;
}

export const ScriptPhase: React.FC<ScriptPhaseProps> = ({
  phase,
  isActive,
  note,
  onNoteChange
}) => {
  const [phaseTimer, setPhaseTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isTimerRunning && isActive) {
      interval = setInterval(() => {
        setPhaseTimer(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, isActive]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimerToggle = () => {
    setIsTimerRunning(!isTimerRunning);
  };

  const resetTimer = () => {
    setPhaseTimer(0);
    setIsTimerRunning(false);
  };

  const getPhaseStatus = () => {
    if (isActive && isTimerRunning) {
      return 'running';
    } else if (isActive) {
      return 'active';
    } else {
      return 'pending';
    }
  };

  const status = getPhaseStatus();

  // Calculate if we're over time
  const isOverTime = phaseTimer > phase.duration;
  const timeRemaining = phase.duration - phaseTimer;

  return (
    <div className="p-6">
      {/* Phase Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`p-2.5 rounded-xl ${
              phase.is_required 
                ? 'bg-emerald-500/15 border border-emerald-500/30' 
                : 'bg-amber-500/15 border border-amber-500/30'
            }`}>
              {phase.is_required ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-400" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">
                {phase.name}
              </h3>
              <div className="flex items-center gap-4 mt-1.5 text-sm">
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Clock className="w-4 h-4" />
                  <span>Expected: <span className="text-slate-200 font-medium">{formatDuration(phase.duration)}</span></span>
                </div>
                {isActive && (
                  <div className={`flex items-center gap-1.5 font-medium ${
                    isOverTime ? 'text-red-400' : 'text-cyan-400'
                  }`}>
                    <Timer className="w-4 h-4" />
                    <span>Actual: {formatDuration(phaseTimer)}</span>
                    {isOverTime && <span className="text-xs text-red-400/80">(+{formatDuration(Math.abs(timeRemaining))})</span>}
                  </div>
                )}
                {phase.is_required && (
                  <span className="px-2.5 py-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold">
                    Required
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {isActive && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleTimerToggle}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                  isTimerRunning
                    ? 'bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500/25'
                    : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25'
                }`}
              >
                {isTimerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                <span className="text-sm">
                  {isTimerRunning ? 'Pause' : 'Start'}
                </span>
              </button>
              <button
                onClick={resetTimer}
                className="p-2.5 bg-slate-700/50 text-slate-400 rounded-xl hover:bg-slate-700/70 hover:text-slate-200 transition-all duration-200 border border-slate-600/50"
                title="Reset Timer"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Status Indicator */}
        <div className={`inline-flex items-center gap-2.5 px-4 py-2 rounded-xl text-sm font-medium ${
          status === 'running'
            ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/40'
            : status === 'active'
            ? 'bg-blue-500/15 text-blue-300 border border-blue-500/40'
            : 'bg-slate-700/50 text-slate-400 border border-slate-600/40'
        }`}>
          <div className={`w-2.5 h-2.5 rounded-full ${
            status === 'running' ? 'bg-cyan-400 animate-pulse shadow-lg shadow-cyan-400/50' :
            status === 'active' ? 'bg-blue-400' : 'bg-slate-500'
          }`}></div>
          <span>
            {status === 'running' ? 'In Progress' :
             status === 'active' ? 'Ready' : 'Pending'}
          </span>
        </div>
      </div>

      {/* Script Content */}
      <div className="mb-6 space-y-4">
        {/* Your Script Box */}
        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-500/30 rounded-2xl p-5 shadow-lg shadow-cyan-500/5">
          <h4 className="font-semibold text-cyan-300 mb-4 flex items-center gap-2.5">
            <div className="p-1.5 bg-cyan-500/20 rounded-lg">
              <MessageSquare className="w-4 h-4" />
            </div>
            Your Script
          </h4>
          <div className="bg-slate-900/60 rounded-xl p-5 border border-slate-700/60">
            <p className="text-slate-200 leading-relaxed whitespace-pre-line text-[15px]">
              {phase.formatted_content || phase.content}
            </p>
          </div>
        </div>

        {/* Questions */}
        {phase.questions && phase.questions.length > 0 && (
          <div className="bg-gradient-to-br from-emerald-500/10 to-green-600/10 border border-emerald-500/30 rounded-2xl p-5 shadow-lg shadow-emerald-500/5">
            <h4 className="font-semibold text-emerald-300 mb-4 flex items-center gap-2.5">
              <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                <HelpCircle className="w-4 h-4" />
              </div>
              Questions to Ask
            </h4>
            <ul className="space-y-3">
              {phase.questions.map((question: string, index: number) => (
                <li key={index} className="flex items-start gap-3 group">
                  <div className="w-6 h-6 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-emerald-500/30 transition-colors">
                    <span className="text-emerald-400 text-sm font-semibold">{index + 1}</span>
                  </div>
                  <span className="text-slate-200 leading-relaxed">{question}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div className="border-t border-slate-700/60 pt-5">
        <label className="flex items-center gap-2 text-sm font-semibold text-white mb-3">
          <span className="text-lg">📝</span>
          Notes <span className="text-slate-500 font-normal">(Optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Add any notes about this phase..."
          className="w-full p-4 border border-slate-600/60 rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 bg-slate-900/60 text-slate-200 placeholder:text-slate-500 resize-none transition-all duration-200"
          rows={3}
        />
        <div className="mt-2 text-xs text-slate-500">
          {note.length} characters
        </div>
      </div>
    </div>
  );
};
